import { useState } from 'react';
import { 
  ChevronDown, 
  ChevronUp, 
  MessageCircle, 
  Clock, 
  User,
  Edit,
  Check,
  AlertCircle,
  ExternalLink
} from 'lucide-react';

const PERSONA_COLORS = {
  riley_ops: 'persona-riley',
  jordan_consults: 'persona-jordan',
  emily_econ: 'persona-emily',
  alex_sells: 'persona-alex',
  priya_pm: 'persona-priya',
};

export default function PostCard({ item, onEdit, onApprove, compact = false }) {
  const [expanded, setExpanded] = useState(!compact);
  const [showFollowups, setShowFollowups] = useState(false);

  const getScoreColor = (score) => {
    if (score >= 8) return 'score-high';
    if (score >= 6) return 'score-medium';
    return 'score-low';
  };

  const getPersonaColor = (personaId) => {
    return PERSONA_COLORS[personaId] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="post-card">
      {/* Header */}
      <div 
        className="p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            {/* Subreddit */}
            <div className="flex items-center space-x-2 text-sm text-gray-500 mb-1">
              <span className="font-medium text-gray-700">{item.subreddit}</span>
              <span>•</span>
              <span>Posted by u/{item.author_username}</span>
              <span>•</span>
              <Clock className="w-3 h-3" />
              <span>{item.timestamp}</span>
            </div>
            
            {/* Title */}
            <h3 className="font-semibold text-gray-900 text-lg leading-tight">
              {item.title}
            </h3>
            
            {/* Tags */}
            <div className="flex items-center space-x-2 mt-2">
              <span className={`badge ${getPersonaColor(item.persona_id)}`}>
                {item.author_username}
              </span>
              {item.flair && (
                <span className="badge bg-blue-100 text-blue-800">
                  {item.flair}
                </span>
              )}
              {item.keyword_ids && (
                <span className="badge bg-gray-100 text-gray-600">
                  {item.keyword_ids}
                </span>
              )}
              {item.score !== undefined && (
                <span className={`score-badge ${getScoreColor(item.score)}`}>
                  Score: {item.score?.toFixed(1)}
                </span>
              )}
              {item.approved && (
                <span className="badge bg-green-100 text-green-800">
                  <Check className="w-3 h-3 mr-1" />
                  Approved
                </span>
              )}
            </div>
          </div>
          
          {/* Expand toggle */}
          <button className="p-1 hover:bg-gray-100 rounded ml-2">
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          {/* Body */}
          {item.body && (
            <div className="mt-3 text-gray-700 whitespace-pre-wrap">
              {item.body}
            </div>
          )}

          {/* Validation warnings */}
          {item.validation?.warnings?.length > 0 && (
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center space-x-2 text-yellow-800 text-sm font-medium mb-1">
                <AlertCircle className="w-4 h-4" />
                <span>Warnings</span>
              </div>
              <ul className="text-sm text-yellow-700 list-disc list-inside">
                {item.validation.warnings.map((warning, idx) => (
                  <li key={idx}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Edit suggestions */}
          {item.edit_suggestions?.length > 0 && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="text-sm font-medium text-blue-800 mb-2">
                Edit Suggestions ({item.edit_suggestions.length})
              </div>
              {item.edit_suggestions.map((suggestion, idx) => (
                <div key={idx} className="text-sm text-blue-700 mb-1">
                  <span className="font-medium">{suggestion.type}:</span>{' '}
                  <span className="line-through text-blue-400">{suggestion.original_snippet}</span>
                  {' → '}
                  <span className="text-blue-900">{suggestion.suggested}</span>
                </div>
              ))}
            </div>
          )}

          {/* Grader feedback */}
          {item.grader_feedback && (
            <div className="mt-3 text-sm text-gray-600 italic">
              "{item.grader_feedback}"
            </div>
          )}

          {/* Followups */}
          {item.followups?.length > 0 && (
            <div className="mt-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFollowups(!showFollowups);
                }}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <MessageCircle className="w-4 h-4 flex-shrink-0" />
                <span>{item.followups.length} Comments</span>
                {showFollowups ? (
                  <ChevronUp className="w-4 h-4 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-4 h-4 flex-shrink-0" />
                )}
              </button>

              {showFollowups && (
                <div className="mt-3 space-y-3">
                  {item.followups.map((followup, idx) => (
                    <div 
                      key={followup.id} 
                      className={`thread-connector ${idx > 0 ? 'ml-4' : ''}`}
                    >
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-500 mb-1">
                        <span className={`badge ${getPersonaColor(followup.persona_id)}`}>
                          u/{followup.username}
                        </span>
                        <span className="hidden sm:inline">•</span>
                        <span>{followup.timestamp}</span>
                        {followup.score && (
                          <span className={`score-badge ${getScoreColor(followup.score)}`}>
                            {followup.score.toFixed(1)}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-700 text-sm sm:text-base">{followup.comment_text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="mt-4 flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100">
            {onEdit && (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(item);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-200 text-gray-800 text-sm font-medium rounded-full hover:bg-gray-300 transition-colors"
              >
                <Edit className="w-4 h-4 flex-shrink-0" />
                <span>Edit</span>
              </button>
            )}
            {onApprove && !item.approved && (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onApprove(item);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-reddit-orange text-white text-sm font-medium rounded-full hover:bg-reddit-orangeLight transition-colors"
              >
                <Check className="w-4 h-4 flex-shrink-0" />
                <span>Approve</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
